/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import {
  getTiposDeNominasEmpleados,
} from 'api/nominas/catalogos/tiposNominasEmpleados';
import {
  getUsuariosPermisosTiposDeNominaEmpleados,
  postUsuariosPermisosTiposDeNominaEmpleados,
  deleteUsuariosPermisosTiposDeNominaEmpleados,
  patchUsuariosPermisosTiposDeNominaEmpleados,
  getUsuarioPermisosTipoDeNominaEmpleado,
} from 'api/nominas/catalogos/usuariosLimitacionesTiposNominaEmpleados';
import { checkToken } from 'api/utils';
import { FLOAT_NUMER_PATTERN } from 'utils/patterns';
import { ESTATUS_AUTORIZADO } from 'utils/constants';

export const usuariosPermisosEmpleadosPermission = {
  permissionModel: 'usuariolimitaciondetipodenominaempleado',
  catalogsModels: [
  ],
  tabsModels: [],
};

const UsuariosPermisosTiposDeNominaEmpleado = ({ permission, selected, disableEditar }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [tipoDeNominaEmpleado, setTipoDeNominaEmpleado] = useState([]);
  const [data, setData] = useState([]);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    // eslint-disable-next-line max-len
    const _data = await getUsuariosPermisosTiposDeNominaEmpleados({ usuario_limitacion_de_informacion: selected });
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _tipoDeNominaEmpleado,
        _data,
      ] = await Promise.all([
        getTiposDeNominasEmpleados({ estados_globales: ESTATUS_AUTORIZADO }),
        getUsuariosPermisosTiposDeNominaEmpleados({ usuario_limitacion_de_informacion: selected }),
      ]);
      if (mounted) {
        setTipoDeNominaEmpleado(_tipoDeNominaEmpleado);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _permiso = await getUsuarioPermisosTipoDeNominaEmpleado(match.id);
    setTimeout(() => {
      if (_permiso) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_permiso));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;

      // eslint-disable-next-line max-len
      const saved = await (key ? patchUsuariosPermisosTiposDeNominaEmpleados(key, { ...values, usuario_limitacion_de_informacion: selected }) : postUsuariosPermisosTiposDeNominaEmpleados({ ...values, usuario_limitacion_de_informacion: selected }));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteUsuariosPermisosTiposDeNominaEmpleados(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Tipo de Nómina Empleado',
      dataIndex: 'tipo_de_nomina_empleado',
      key: 'tipo_de_nomina_empleado',
      width: 200,
      render: (val) => tipoDeNominaEmpleado.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    periodo: [requiredRule],
    valor: [
      requiredRule,
      {
        pattern: FLOAT_NUMER_PATTERN,
        message: 'Ingrese un valor válido',
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          allowExportPDF={false}
          allowAdd={!disableEditar}
          allowDelete={!disableEditar}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.lenght ? 'Editar' : 'Agregar'} Permisos Tipo de Nómina Empleado`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableEditar}
            />
            )}
          onCancel={onCancel}
          forceRender
          width="45%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  label="Tipo de Nómina Empleado"
                  name="tipo_de_nomina_empleado"
                  rules={rules.periodo}
                  hasFeedback
                >
                  <Select
                    dataSource={tipoDeNominaEmpleado}
                    disabled={disableEditar}
                  />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`El Tipo De Nómina Empleado ${tipoDeNominaEmpleado.find((e) => e.id === form.getFieldValue('tipo_de_nomina_empleado'))?.descripcion}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default UsuariosPermisosTiposDeNominaEmpleado;
